import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import Color from '@tiptap/extension-color';
import './AdminBlog.css';

const AdminBlog = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category_id: '',
    status: 'published',
    show_on_homepage: false,
    featured_image: null,
    featured_image_url: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [useImageUrl, setUseImageUrl] = useState(false);

  // Custom Font Size Extension
  const CustomFontSize = TextStyle.extend({
    addAttributes() {
      return {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize,
          renderHTML: attributes => {
            if (!attributes.fontSize) return {};
            return {
              style: `font-size: ${attributes.fontSize}`,
            };
          },
        },
      };
    },
  });

  // TipTap Editor with Font Support
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'blog-paragraph',
          },
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'blog-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'blog-link',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
        defaultAlignment: 'left',
      }),
      Highlight,
      Typography,
      Placeholder.configure({
        placeholder: 'Write your blog content here...',
      }),
      TextStyle,
      FontFamily,
      Color,
      CustomFontSize,
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
  });

  // Update editor content when editing post changes
  useEffect(() => {
    if (editor && formData.content !== editor.getHTML()) {
      editor.commands.setContent(formData.content);
    }
  }, [editor, formData.content]);

  const canManage = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchComments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/blog/admin/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blog/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get('/blog/admin/comments');
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, featured_image: file, featured_image_url: '' });
      setImagePreview(URL.createObjectURL(file));
      setUseImageUrl(false);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, featured_image_url: url, featured_image: null });
    if (url) {
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('content', formData.content);
    submitData.append('excerpt', formData.excerpt);
    submitData.append('category_id', formData.category_id);
    submitData.append('status', formData.status);
    submitData.append('show_on_homepage', formData.show_on_homepage);

    if (useImageUrl && formData.featured_image_url) {
      submitData.append('featured_image_url', formData.featured_image_url);
    } else if (formData.featured_image) {
      submitData.append('featured_image', formData.featured_image);
    }

    setLoading(true);
    try {
      if (editingPost) {
        await api.put(`/blog/admin/posts/${editingPost.id}`, submitData);
        toast.success('Post updated successfully');
      } else {
        await api.post('/blog/admin/posts', submitData);
        toast.success('Post created successfully');
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.message || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category_id: post.category_id || '',
      status: post.status,
      show_on_homepage: post.show_on_homepage,
      featured_image: null,
      featured_image_url: post.featured_image || ''
    });

    if (post.featured_image) {
      let fullUrl = post.featured_image;
      if (!fullUrl.startsWith('http')) {
        fullUrl = `https://graduates.katinetti.ac.ke${fullUrl}`;
      }
      setImagePreview(fullUrl);
      setUseImageUrl(fullUrl.startsWith('http'));
    }
    setShowForm(true);
    setActiveDropdown(null);
  };

  const handleDelete = async (id) => {
    setActiveDropdown(null);
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.delete(`/blog/admin/posts/${id}`);
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleApproveComment = async (id) => {
    try {
      await api.put(`/blog/admin/comments/${id}/approve`);
      toast.success('Comment approved successfully');
      fetchComments();
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('Failed to approve comment');
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/blog/admin/comments/${id}`);
      toast.success('Comment deleted successfully');
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category_id: '',
      status: 'published',
      show_on_homepage: false,
      featured_image: null,
      featured_image_url: ''
    });
    setImagePreview(null);
    setUseImageUrl(false);
    if (editor) {
      editor.commands.setContent('');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        const formData = new FormData();
        formData.append('image', file);
        try {
          const response = await api.post('/blog/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const imageUrl = response.data.fullUrl || `https://graduates.katinetti.ac.ke${response.data.url}`;
          editor.chain().focus().setImage({ src: imageUrl }).run();
          toast.success('Image inserted successfully');
        } catch (error) {
          console.error('Image upload failed:', error);
          toast.error('Failed to upload image');
        }
      }
    };
    input.click();
  };

  const handleInsertImageUrl = () => {
    const url = window.prompt('Enter complete image URL (including https://):');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      editor.chain().focus().setImage({ src: url }).run();
      toast.success('Image inserted from URL');
    } else if (url) {
      toast.error('Please enter a valid URL starting with http:// or https://');
    }
  };

  // Font families
  const fontFamilies = [
    { name: 'Default', value: '' },
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, serif' },
    { name: 'Courier New', value: 'Courier New, monospace' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Verdana', value: 'Verdana, sans-serif' },
    { name: 'Helvetica', value: 'Helvetica, sans-serif' },
    { name: 'Poppins', value: 'Poppins, sans-serif' },
  ];

  // Font sizes
  const fontSizes = [
    '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '42px', '48px'
  ];

  // Text colors
  const textColors = [
    '#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#4A5568', '#E53E3E', '#38A169', '#3182CE', '#D69E2E', '#805AD5', '#DD6B20'
  ];

  // Toolbar component
  const Toolbar = () => {
    if (!editor) return null;

    return (
      <div className="tiptap-toolbar">
        <div className="toolbar-group">
          <select
            onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
            className="toolbar-select"
            defaultValue=""
          >
            {fontFamilies.map(font => (
              <option key={font.value} value={font.value}>{font.name}</option>
            ))}
          </select>

          <select
            onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
            className="toolbar-select"
            defaultValue="16px"
          >
            {fontSizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>

          <select
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="toolbar-select"
            defaultValue="#000000"
          >
            <option value="#000000">Black</option>
            {textColors.map(color => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </select>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'active' : ''}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'active' : ''}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? 'active' : ''}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={editor.isActive('highlight') ? 'active' : ''}
            title="Highlight"
          >
            <span style={{ backgroundColor: '#fef08a' }}>H</span>
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'active' : ''}
            title="Bullet List"
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'active' : ''}
            title="Numbered List"
          >
            1. List
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''}
            title="Align Left"
          >
            ⬅️
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''}
            title="Center"
          >
            ⬌
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''}
            title="Align Right"
          >
            ➡️
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}
            title="Justify"
          >
            ↔️
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'active' : ''}
            title="Quote"
          >
            ❝
          </button>
          <button
            type="button"
            onClick={handleImageUpload}
            title="Upload Image"
          >
            📷
          </button>
          <button
            type="button"
            onClick={handleInsertImageUrl}
            title="Insert Image from URL"
          >
            🔗📷
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt('Enter URL:');
              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                editor.chain().focus().setLink({ href: url }).run();
                toast.success('Link added');
              } else if (url) {
                toast.error('Please enter a valid URL');
              }
            }}
            className={editor.isActive('link') ? 'active' : ''}
            title="Insert Link"
          >
            🔗
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            title="Undo"
          >
            ↩️
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            title="Redo"
          >
            ↪️
          </button>
        </div>
      </div>
    );
  };

  // Actions Menu Component using React Portal
  const ActionsMenu = ({ postId, onEdit, onDelete }) => {
    if (activeDropdown !== postId) return null;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 min-w-40"
        style={{
          top: `${window.event?.clientY || 0}px`,
          left: `${window.event?.clientX || 0}px`,
        }}
      >
        <button
          onClick={onEdit}
          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
        >
          <span>✏️</span> Edit
        </button>
        <button
          onClick={onDelete}
          className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
        >
          <span>🗑️</span> Delete
        </button>
      </div>,
      document.body
    );
  };

  const handleThreeDotsClick = (e, postId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === postId ? null : postId);
  };

  if (!canManage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-12">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Access Denied</h3>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Blog & News Management</h1>
          <p className="text-gray-600 mt-1">Manage posts, categories, and comments</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Write New Post'}
        </Button>
      </div>

      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'posts' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Posts ({posts.length})
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'comments' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Comments ({comments.filter(c => !c.is_approved).length} pending)
        </button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
          <form onSubmit={handleSubmit}>
            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />

            <div className="grid grid-cols-2 gap-4 my-4">
              <select
                className="input-field"
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              >
                <option value="">Select Category</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <label className="flex items-center gap-2 my-2">
              <input
                type="checkbox"
                checked={formData.show_on_homepage}
                onChange={(e) => setFormData({...formData, show_on_homepage: e.target.checked})}
              />
              Show on Homepage
            </label>

            <textarea
              className="input-field my-2"
              rows="3"
              placeholder="Excerpt (brief summary of the post)"
              value={formData.excerpt}
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
            />

            <div className="my-4 border border-gray-300 rounded-lg overflow-hidden">
              <Toolbar />
              <div className="p-4 min-h-[400px] bg-white">
                <EditorContent editor={editor} />
              </div>
            </div>

            <div className="my-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!useImageUrl}
                    onChange={() => setUseImageUrl(false)}
                  />
                  Upload File
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={useImageUrl}
                    onChange={() => setUseImageUrl(true)}
                  />
                  Use Image URL
                </label>
              </div>

              {!useImageUrl ? (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                />
              ) : (
                <input
                  type="text"
                  placeholder="https://graduates.katinetti.ac.ke/uploads/blog/your-image.jpg"
                  value={formData.featured_image_url}
                  onChange={handleImageUrlChange}
                  className="input-field"
                />
              )}

              {imagePreview && (
                <div className="mt-4">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-48 h-48 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200?text=Invalid+URL';
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Preview</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingPost ? 'Update Post' : 'Publish Post')}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {activeTab === 'posts' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Homepage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map(post => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-left font-medium">{post.title}</td>
                  <td className="px-4 py-2 text-left">{post.category_name || '-'}</td>
                  <td className="px-4 py-2 text-left">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(post.status)}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-left">{post.show_on_homepage ? '✅' : '❌'}</td>
                  <td className="px-4 py-2 text-left">{post.view_count || 0}</td>
                  <td className="px-4 py-2 text-left">{new Date(post.published_at || post.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-left relative">
                    <button
                      onClick={(e) => handleThreeDotsClick(e, post.id)}
                      className="text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100 text-xl"
                    >
                      ⋯
                    </button>
                    <ActionsMenu
                      postId={post.id}
                      onEdit={() => handleEdit(post)}
                      onDelete={() => handleDelete(post.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'comments' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comments.map(comment => (
                <tr key={comment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-left">{comment.post_title}</td>
                  <td className="px-4 py-2 text-left">{comment.name}</td>
                  <td className="px-4 py-2 text-left">{comment.email}</td>
                  <td className="px-4 py-2 text-left max-w-xs truncate">{comment.comment}</td>
                  <td className="px-4 py-2 text-left">
                    <span className={`px-2 py-1 text-xs rounded-full ${comment.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {comment.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-left">
                    {!comment.is_approved && (
                      <button
                        onClick={() => handleApproveComment(comment.id)}
                        className="text-green-600 mr-3 hover:text-green-800 text-sm font-medium"
                      >
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
